import { FunctionComponent } from 'react';
import {
  AutomationTypeEnum,
  Fee,
  FeeFixed,
  FeeType,
  Requirement,
} from '../types';
import { useGlobalContext } from '../contexts/globalContext';
import { isDomestic } from '../utils/legal-entity';

type PlatformFeeInformationContentProps = {
  requirement: Requirement;
  fee?: Fee;
  baseFee: number;
  displayRegisteredAgentFee: boolean;
  platformName: string;
};

const PlatformFeeInformationContent: FunctionComponent<
  PlatformFeeInformationContentProps
> = ({
  requirement,
  fee,
  baseFee,
  displayRegisteredAgentFee,
  platformName,
}) => {
  let totalFee = baseFee;
  let feeInformation = '';
  if (fee && fee.type == FeeType.Fixed) {
    const feeAmount = (fee as FeeFixed).amount as unknown as number;
    totalFee += +feeAmount;
  }

  const tags = requirement.tags || [];
  const isForeignQualification = tags.find(
    (tag) => tag == AutomationTypeEnum.ForeignQualification
  );
  const isAnnualReport = tags.find(
    (tag) => tag == AutomationTypeEnum.AnnualReport
  );
  const isInitialStatementOfInformation = tags.find(
    (tag) => tag == AutomationTypeEnum.InitialStatement
  );
  const isLocalPayrollSetup = tags.find(
    (tag) => tag == AutomationTypeEnum.LocalPayrollSetup
  );
  const isPayrollSetup = tags.find(
    (tag) => tag == AutomationTypeEnum.PayrollSetup
  );

  if (isForeignQualification) {
    feeInformation = `Registration costs $${totalFee}`;
  } else if (isAnnualReport || isInitialStatementOfInformation) {
    feeInformation = `Filing fee costs $${totalFee}`;
  } else if (isLocalPayrollSetup) {
    feeInformation = `Registration costs $${totalFee}`;
  } else if (isPayrollSetup) {
    feeInformation = `Registration costs $${totalFee}`;
  } else {
    feeInformation = `Filing fee costs $${totalFee}`;
  }
  if (fee && (fee.type === FeeType.Variable || fee.type === FeeType.Range)) {
    feeInformation += ` plus filing fees calculated by the state based on your business`;
  }
  return (
    <div className="mt-2 text-sm">
      <strong>
        {feeInformation}{' '}
        {displayRegisteredAgentFee && (
          <>and $10/month for ongoing registered agent service,</>
        )}{' '}
        invoiced by {platformName}.
      </strong>{' '}
      Fees are estimated based on state, filing and shipping costs if required,
      and may change.
    </div>
  );
};

type PlatformFeeInformationProps = {
  requirement: Requirement;
  regionCode: string;
  baseFee: number;
  displayRegisteredAgentFee: boolean;
  domesticFee?: Fee;
  foreignFee?: Fee;
};

export const PlatformFeeInformation: FunctionComponent<
  PlatformFeeInformationProps
> = ({
  requirement,
  regionCode,
  baseFee,
  displayRegisteredAgentFee,
  domesticFee,
  foreignFee,
}) => {
  const { globalState } = useGlobalContext();
  const isLegalEntityDomestic = isDomestic(
    globalState.user.legal_entity,
    regionCode
  );
  const platformName =
    globalState.user.legal_entity.platforms?.[0]?.name || 'Gusto';
  if (globalState.user.is_platform_user) {
    return (
      <PlatformFeeInformationContent
        requirement={requirement}
        fee={isLegalEntityDomestic ? domesticFee : foreignFee}
        baseFee={baseFee}
        displayRegisteredAgentFee={displayRegisteredAgentFee}
        platformName={platformName}
      />
    );
  }
  return null;
};
